import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, Spinner, Alert, InputGroup, Pagination, ButtonGroup } from 'react-bootstrap';
import apiClient from '../../../utils/axios';
import { format, addDays, addMonths, addYears, parseISO, differenceInDays } from 'date-fns';
import { zhTW } from 'date-fns/locale';

const SubscriptionManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [expireDate, setExpireDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('無');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('expireDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expiryFilter, setExpiryFilter] = useState('has-expiry'); // 新增篩選狀態: 'all', 'has-expiry', 'expired', 'near-expiry'

  // 付款方式選項
  const paymentOptions = ['無', '支付寶', '銀行轉帳', 'PayPal'];

  // 加載訂閱資料
  const fetchSubscriptions = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      // 將前端的駝峰命名轉換為後端的蛇形命名
      let backendSortField = sortField;
      if (sortField === 'registrationDate') backendSortField = 'registration_date';
      if (sortField === 'expireDate') backendSortField = 'expire_date';

      // axios interceptor 已經自動添加 Authorization header
      const response = await apiClient.get(`/admin/users`, {
        params: {
          page,
          search: searchTerm,
          sort: backendSortField,
          direction: sortDirection,
          filter: expiryFilter
        }
      });

      // API 直接返回數據，不包含 success 字段
      setUsers(response.data.users || []);
      setCurrentPage(response.data.currentPage || page);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('獲取訂閱資料錯誤:', error);
      setError('獲取訂閱資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, sortField, sortDirection, expiryFilter]);

  useEffect(() => {
    fetchSubscriptions(1);
  }, [sortField, sortDirection, expiryFilter, searchTerm, fetchSubscriptions]);

  // 處理排序
  const handleSort = (field) => {
    if (sortField === field) {
      // 如果已經按照這個欄位排序，切換排序方向
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 設置新的排序欄位，並設置為升序排列
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // 排序圖標
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
    }
    return sortDirection === 'asc'
      ? <i className="bi bi-arrow-down text-primary ms-1"></i>
      : <i className="bi bi-arrow-up text-primary ms-1"></i>;
  };

  // 打開編輯模態框
  const handleEdit = (user) => {
    setCurrentUser(user);
    // 格式化日期為 yyyy-MM-dd 格式以符合 HTML5 date input
    if (user.expireDate && user.expireDate !== 'permanent') {
      try {
        const formattedDate = format(parseISO(user.expireDate), 'yyyy-MM-dd');
        setExpireDate(formattedDate);
      } catch (error) {
        setExpireDate(user.expireDate || '');
      }
    } else {
      setExpireDate(user.expireDate || '');
    }
    setPaymentMethod(user.paymentMethod || '無');
    setShowModal(true);
  };

  // 增加到期日
  const handleAddTime = (amount, unit) => {
    let newDate;
    let baseDate;

    if (expireDate && expireDate !== 'permanent') {
      try {
        baseDate = parseISO(expireDate);
      } catch (error) {
        // 如果解析失敗，使用當前日期
        baseDate = new Date();
      }
    } else {
      // 如果沒有到期日或是永久，使用當前日期
      baseDate = new Date();
    }

    switch (unit) {
      case 'month':
        newDate = addMonths(baseDate, amount);
        break;
      case 'year':
        newDate = addYears(baseDate, amount);
        break;
      case 'day':
      default:
        newDate = addDays(baseDate, amount);
    }

    setExpireDate(format(newDate, 'yyyy-MM-dd'));
  };

  // 設為永久
  const handleSetPermanent = () => {
    setExpireDate('permanent');
  };

  // 更新訂閱
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.patch(`/admin/users/${currentUser.id}/subscriptions`, {
        expireDate,
        paymentMethod
      });

      if (response.status === 204) {
        setSuccessMessage('訂閱資訊更新成功！');

        const updatedUsers = users.map(user =>
          user.id === currentUser.id
            ? { ...user, expireDate: expireDate, paymentMethod: paymentMethod }
            : user
        );
        setUsers(updatedUsers);

        setTimeout(() => {
          setShowModal(false);
          setSuccessMessage('');
        }, 2000);
      } else {
        setError('更新訂閱資訊失敗');
      }
    } catch (error) {
      console.error('更新訂閱時發生錯誤:', error);
      setError('更新訂閱資訊時發生錯誤');
    }
  };

  // 格式化到期日顯示
  const formatExpireDate = (date) => {
    if (!date) return '未設定';
    if (date === 'permanent') return '永久';
    
    try {
      return format(parseISO(date), 'yyyy年MM月dd日', { locale: zhTW });
    } catch (error) {
      return date;
    }
  };

  // 計算是否過期
  const isExpired = (date) => {
    if (!date || date === 'permanent') return false;
    
    try {
      const expireDate = parseISO(date);
      return expireDate < new Date();
    } catch (error) {
      return false;
    }
  };
  
  // 檢查是否快要到期（3天內）
  const isNearExpiry = (date) => {
    if (!date || date === 'permanent') return false;
    
    try {
      const expireDate = parseISO(date);
      const today = new Date();
      
      // 如果已過期，則返回 false（因為這種情況會另外顯示為已過期）
      if (expireDate < today) return false;
      
      // 計算到期日與今天的天數差
      const daysRemaining = differenceInDays(expireDate, today);
      
      // 如果剩餘天數小於等於 3，則視為快要到期
      return daysRemaining <= 3;
    } catch (error) {
      return false;
    }
  };

  // 處理搜尋
  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubscriptions(1);
  };

  // 處理分頁
  const handlePageChange = (page) => {
    fetchSubscriptions(page);
  };

  // 處理篩選變更
  const handleFilterChange = (filter) => {
    setExpiryFilter(filter);
    setCurrentPage(1); // 重置為第一頁
  };

  // 分頁組件
  const PaginationComponent = () => {
    const pageItems = [];
    for (let i = 1; i <= totalPages; i++) {
      pageItems.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="mt-3 justify-content-center">
        <Pagination.Prev 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        />
        {pageItems}
        <Pagination.Next 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  return (
    <div className="subscription-management p-4">
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <h3 className="mb-0">用戶訂閱管理</h3>
        <div className="d-flex">
          <Form onSubmit={handleSearch} className="d-flex">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="搜尋用戶..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="primary" type="submit">
                搜尋
              </Button>
            </InputGroup>
          </Form>
          <Button 
            variant="outline-primary" 
            onClick={() => fetchSubscriptions(currentPage)} 
            className="d-flex align-items-center ms-2"
          >
            <i className="bi bi-arrow-clockwise me-1"></i> 重新整理
          </Button>
        </div>
      </div>

      {/* 篩選選項 */}
      <div className="mb-4">
        <ButtonGroup>
          <Button 
            variant={expiryFilter === 'all' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('all')}
          >
            全部
          </Button>
          <Button 
            variant={expiryFilter === 'has-expiry' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('has-expiry')}
          >
            有到期日
          </Button>
          <Button 
            variant={expiryFilter === 'expired' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('expired')}
          >
            已到期
          </Button>
          <Button 
            variant={expiryFilter === 'near-expiry' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('near-expiry')}
          >
            快到期
          </Button>
        </ButtonGroup>
      </div>

      {/* 加載狀態 */}
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">正在加載訂閱資料...</p>
        </div>
      ) : (
        <>
          {/* 桌面版表格 */}
          <div className="table-responsive">
            <Table hover className="align-middle border">
              <thead className="bg-light">
                <tr>
                  <th style={{ width: '50px' }}>#</th>
                  <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                    用戶名稱 {renderSortIcon('username')}
                  </th>
                  <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                    電子郵件 {renderSortIcon('email')}
                  </th>
                  <th onClick={() => handleSort('registrationDate')} style={{ cursor: 'pointer' }}>
                    註冊日期 {renderSortIcon('registrationDate')}
                  </th>
                  <th onClick={() => handleSort('expireDate')} style={{ cursor: 'pointer' }}>
                    到期日 {renderSortIcon('expireDate')}
                  </th>
                  <th onClick={() => handleSort('paymentMethod')} style={{ cursor: 'pointer' }}>
                    付款方式 {renderSortIcon('paymentMethod')}
                  </th>
                  <th onClick={() => handleSort('isActive')} style={{ cursor: 'pointer' }}>
                    帳戶狀態 {renderSortIcon('isActive')}
                  </th>
                  <th style={{ width: '100px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user, idx) => (
                    <tr key={user.id}>
                      <td>{(currentPage - 1) * 10 + idx + 1}</td>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{new Date(user.registrationDate).toLocaleDateString()}</td>
                      <td>
                        {isExpired(user.expireDate) ? (
                          <Badge bg="danger">已過期 ({formatExpireDate(user.expireDate)})</Badge>
                        ) : isNearExpiry(user.expireDate) ? (
                          <Badge bg="warning">快到期 ({formatExpireDate(user.expireDate)})</Badge>
                        ) : (
                          <span className={!user.expireDate ? 'text-muted' : ''}>
                            {formatExpireDate(user.expireDate)}
                          </span>
                        )}
                      </td>
                      <td>{user.paymentMethod || '無'}</td>
                      <td>
                        {user.isActive ? (
                          <Badge bg="success">已啟用</Badge>
                        ) : (
                          <Badge bg="warning">未啟用</Badge>
                        )}
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => handleEdit(user)}
                        >
                          編輯
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      {searchTerm ? '沒有符合搜尋條件的用戶' : '沒有用戶資料'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          
          {/* 分頁 */}
          {totalPages > 1 && <PaginationComponent />}
        </>
      )}

      {/* 編輯訂閱模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            編輯訂閱設定 - {currentUser?.username}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {successMessage && (
              <Alert variant="success" className="mb-3">
                {successMessage}
              </Alert>
            )}
            
            <Row className="mb-3">
              <Col xs={12} md={6} className="mb-3 mb-md-0">
                <Form.Group>
                  <Form.Label>付款方式</Form.Label>
                  <Form.Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    {paymentOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>到期日</Form.Label>
                  <Form.Control
                    type={expireDate === 'permanent' ? 'text' : 'date'}
                    value={expireDate === 'permanent' ? '永久' : expireDate}
                    onChange={(e) => setExpireDate(e.target.value)}
                    disabled={expireDate === 'permanent'}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button 
                variant="outline-primary" 
                size="sm"
                type="button"
                onClick={() => handleAddTime(1, 'month')}
              >
                增加 1 個月
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm"
                type="button"
                onClick={() => handleAddTime(1, 'year')}
              >
                增加 1 年
              </Button>
              <Button 
                variant="outline-success" 
                size="sm"
                type="button"
                onClick={handleSetPermanent}
              >
                設為永久
              </Button>
              {expireDate === 'permanent' && (
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  type="button"
                  onClick={() => setExpireDate(format(new Date(), 'yyyy-MM-dd'))}
                >
                  取消永久
                </Button>
              )}
            </div>
            
            <Alert variant="info" className="mb-0">
              <i className="bi bi-info-circle-fill me-2"></i>
              設定為永久表示該用戶無到期日限制
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              取消
            </Button>
            <Button variant="primary" type="submit">
              儲存變更
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionManagement;